import {
  debounce,
  zxcvbnAsync,
  zxcvbnOptions,
  ZxcvbnResult,
} from '@zxcvbn-ts/core';
import { matcherPwnedFactory } from '@zxcvbn-ts/matcher-pwned';
import { useDeferredValue, useEffect, useState } from 'react';

export enum PasswordStrengthOptions {
  Weakest = 'weakest',
  Weak = 'weak',
  Good = 'good',
  Strong = 'strong',
  Strongest = 'strongest',
}

const USER_INPUTS = 2000;
const STRENGTH_LABELS = [
  PasswordStrengthOptions.Weakest,
  PasswordStrengthOptions.Weak,
  PasswordStrengthOptions.Good,
  PasswordStrengthOptions.Strong,
  PasswordStrengthOptions.Strongest,
];

interface PasswordStrength {
  score: number;
  feedback: {
    suggestions: string[];
    warning: string;
  };
  strengthLabel: string;
}

let optionsReady: Promise<void> | null = null;

export const getZXCVBNOptions = async (password: string): Promise<unknown> => {
  if (optionsReady === null) {
    const matcherPwned = matcherPwnedFactory(fetch, zxcvbnOptions);
    zxcvbnOptions.addMatcher('pwned', matcherPwned);

    optionsReady = Promise.all([
      import('@zxcvbn-ts/language-common'),
      import('@zxcvbn-ts/language-en'),
    ]).then(([common, en]) => {
      zxcvbnOptions.setOptions({
        dictionary: { ...common.dictionary, ...en.dictionary },
        graphs: common.adjacencyGraphs,
        translations: en.translations,
      });
    });
  }

  await optionsReady;
  return debounce(() => zxcvbnAsync(password), USER_INPUTS, true)();
};

export const usePasswordStrength = (password: string): PasswordStrength => {
  const [result, setResult] = useState<ZxcvbnResult>({} as ZxcvbnResult);
  const deferredPassword = useDeferredValue(password);

  useEffect(() => {
    getZXCVBNOptions(deferredPassword).then((zxcvbnResult) =>
      setResult(zxcvbnResult as ZxcvbnResult),
    );
  }, [deferredPassword]);

  return {
    score: result.score || 0,
    strengthLabel: STRENGTH_LABELS[result.score] || 'weakest',
    feedback: {
      suggestions: result.feedback?.suggestions || [],
      warning: result.feedback?.warning || '',
    },
  };
};
